import { useEffect, useRef, useState } from "react";
import { Dropdown } from "antd";
import {
    AlertTriangle, Building, CalendarClock, Check, ChevronDown, ChevronLeft, ChevronRight,
    Loader2, Mail, MessageCircle, MoreHorizontal, Phone, StickyNote, TrendingUp,
} from "lucide-react";
import dayjs from "dayjs";

import CallButton from "../../telephony/CallButton";
import { cn } from "../../../utils/tailwindUtils";
import { getStageBadgeClass } from "../../../utils/leadStageBadge";
import { formatDateTime } from "../../../utils/datetime";
import LeadQualityBadges from "../LeadQualityBadges";

/*
 * Presentation pieces of the Customer Detail page (pages/customers/
 * CustomerDetails.js). They hold no data and fetch nothing: the page passes in
 * what it already has and the handlers it already uses, so moving the layout
 * around never changes what a save does.
 */

// ── stages ───────────────────────────────────────────────────────────────

/* The main path a prospect moves along. The other stages (Unanswered, No
   Show, On Hold, Closed Lost, Dis-Qualified) are states beside the path, not
   steps on it, so the stepper shows them as a chip next to the furthest step
   the lead has actually reached. */
export const PROSPECT_PATH = ["New Lead", "Contacted", "Qualified", "Demo Fixed", "Demo Done",
                              "Proposal", "Negotiation", "Closed Won"];
export const PARTNER_PATH = ["Onboarding", "Agreement Signed", "Active"];
const LOST_STAGES = ["Closed Lost", "Dis-Qualified", "Terminated", "Suspended"];

/** Index on `path` of the furthest stage reached: the current one if it is on
 *  the path, else the latest on-path stage in the lead's history. */
export function reachedIndex(path, stage, history = []) {
    if (stage === "Onboarded") return path.length - 1;
    const here = path.indexOf(stage);
    if (here >= 0) return here;
    let best = -1;
    for (const log of history) best = Math.max(best, path.indexOf(log?.to_status));
    return best;
}

export function StageProgress({ stage, history, isChannelPartner, onPick, disabled }) {
    const path = isChannelPartner ? PARTNER_PATH : PROSPECT_PATH;
    const reached = reachedIndex(path, stage, history);
    const offPath = stage && !path.includes(stage) && stage !== "Onboarded";
    const lost = LOST_STAGES.includes(stage);
    // On a narrow screen the strip scrolls: bring the lead's own step (or the
    // "Now:" chip) into view. scrollLeft, so the page itself never moves.
    const navRef = useRef(null);
    useEffect(() => {
        const nav = navRef.current;
        if (!nav || nav.scrollWidth <= nav.clientWidth) return;
        const target = offPath ? nav.lastElementChild : nav.querySelectorAll("li")[Math.max(reached, 0)];
        if (!target) return;
        nav.scrollLeft = target.offsetLeft - nav.offsetLeft - (nav.clientWidth - target.offsetWidth) / 2;
    }, [stage, reached, offPath]);
    return (
        <nav ref={navRef} aria-label="Sales progress" className="flex items-center gap-2 overflow-x-auto pb-1">
            <ol className="flex items-center min-w-max">
                {path.map((s, i) => {
                    // Reached before and since left the path (e.g. On Hold after a
                    // demo), or onboarded past the end: done. The lead's own stage
                    // on the path: current.
                    const done = i < reached || (i === reached && (offPath || stage === "Onboarded"));
                    const current = i === reached && !done;
                    return (
                        <li key={s} className="flex items-center">
                            {i > 0 && <span className={cn("h-px w-4 sm:w-6", i <= reached ? "bg-primary" : "bg-gray-300 dark:bg-gray-600")} />}
                            <button type="button" disabled={disabled} onClick={() => onPick?.(s)}
                                title={`Move to ${s}`} aria-current={current ? "step" : undefined}
                                className={cn("flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap transition-colors",
                                    current ? "border-primary bg-primary text-white"
                                        : done ? "border-primary/40 bg-brand-50 text-primary dark:bg-brand-900/20"
                                        : "border-gray-200 text-gray-500 hover:border-primary/50 hover:text-primary dark:border-gray-600 dark:text-gray-400",
                                    disabled && "cursor-default")}>
                                {done && <Check className="h-3 w-3" />}
                                {s}
                            </button>
                        </li>
                    );
                })}
            </ol>
            {offPath && (
                <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
                    lost ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" : getStageBadgeClass(stage))}>
                    Now: {stage}
                </span>
            )}
        </nav>
    );
}

// ── header ───────────────────────────────────────────────────────────────

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const POTENTIAL_ICON = { Hot: "🔥", Medium: "⚡", Cold: "❄️" };

export function CustomerHeader({ customer, ownerName, valueAmount, valueLabel, refreshing,
                                 onBack, onWhy, actions, statusRow }) {
    const potential = customer.contract_details?.potential;
    const place = [customer.city, customer.state].filter(Boolean).join(", ");
    return (
        <header className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-wrap items-start gap-3">
                <button type="button" onClick={onBack} aria-label="Back"
                    className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary sm:flex">
                    <Building className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h1 className="truncate text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                            {customer.customer_name || "Customer"}
                        </h1>
                        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", getStageBadgeClass(customer.lead_stage))}>
                            {customer.lead_stage || "No stage"}
                        </span>
                        {potential && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {POTENTIAL_ICON[potential] || ""} {potential}
                            </span>
                        )}
                        <LeadQualityBadges row={customer} size="lg" />
                        {customer.is_active === false && (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">Inactive</span>
                        )}
                        {refreshing && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" aria-label="Refreshing" />}
                    </div>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                        {customer.customer_type && <span>{customer.customer_type}</span>}
                        {place && <><span aria-hidden>·</span><span>{place}</span></>}
                        <span aria-hidden>·</span>
                        <span>Owner: <b className="font-medium text-gray-700 dark:text-gray-200">{ownerName || "Unassigned"}</b></span>
                        <button type="button" onClick={onWhy} className="text-primary hover:underline">Why?</button>
                    </p>
                    {statusRow}
                </div>
                {valueAmount != null && (
                    <div className="text-right">
                        <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{money(valueAmount)}</p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">{valueLabel}</p>
                    </div>
                )}
            </div>
            {actions && <div className="mt-3 hidden md:block">{actions}</div>}
        </header>
    );
}

const actionBtn = "inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700";

/** Call (with every number on file), WhatsApp, Email, Note, Stage, More. */
export function QuickActions({ phones = [], customerId, onCalled, onWhatsApp, onEmail, onNote,
                               onStage, moreItems = [] }) {
    const [first, ...others] = phones;
    return (
        <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Customer actions">
            {first ? (
                <span className="inline-flex items-center">
                    <CallButton customerId={customerId} number={first.number} onCalled={onCalled}
                        className={cn("h-8 text-xs", others.length && "rounded-r-none")} />
                    {others.length > 0 && (
                        <Dropdown trigger={["click"]} popupRender={() => (
                            <div className="min-w-[14rem] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                {phones.map((p) => (
                                    <div key={p.number} className="flex items-center justify-between gap-3 rounded px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <span className="min-w-0">
                                            <span className="block truncate font-medium text-gray-800 dark:text-gray-100">{p.label}</span>
                                            <span className="text-gray-500">{p.number}</span>
                                        </span>
                                        <CallButton size="xs" customerId={customerId} number={p.number} onCalled={onCalled} />
                                    </div>
                                ))}
                            </div>
                        )}>
                            <button type="button" aria-label="Call another number"
                                className="inline-flex h-8 items-center rounded-r-md border border-l-0 border-gray-300 px-1.5 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
                                <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                        </Dropdown>
                    )}
                </span>
            ) : (
                <button type="button" disabled className={actionBtn} title="No phone number on file">
                    <Phone className="h-3.5 w-3.5" /> Call
                </button>
            )}
            {onWhatsApp && (
                <button type="button" onClick={onWhatsApp} className={actionBtn}>
                    <MessageCircle className="h-3.5 w-3.5 text-green-600" /> WhatsApp
                </button>
            )}
            {onEmail && (
                <button type="button" onClick={onEmail} className={actionBtn}>
                    <Mail className="h-3.5 w-3.5 text-sky-600" /> Email
                </button>
            )}
            <button type="button" onClick={onNote} className={actionBtn}>
                <StickyNote className="h-3.5 w-3.5 text-amber-500" /> Note
            </button>
            <button type="button" onClick={onStage}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-white hover:opacity-90">
                <TrendingUp className="h-3.5 w-3.5" /> Update stage / follow-up
            </button>
            {moreItems.length > 0 && (
                <Dropdown trigger={["click"]}
                    menu={{ items: moreItems.map((m) => ({ key: m.key, label: m.label, danger: m.danger, onClick: m.onClick })) }}>
                    <button type="button" className={actionBtn} aria-label="More actions">
                        <MoreHorizontal className="h-3.5 w-3.5" /> More
                    </button>
                </Dropdown>
            )}
        </div>
    );
}

/** Phone-sized bar pinned to the bottom of the screen: the four things a rep
 *  does most, always within reach of a thumb. */
export function MobileActionBar({ phone, customerId, onCalled, onWhatsApp, onNote, onStage }) {
    const cell = "flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-medium text-gray-600 dark:text-gray-300";
    return (
        <div className="fixed inset-x-0 bottom-0 z-40 flex border-t border-gray-200 bg-white/95 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 md:hidden"
             role="toolbar" aria-label="Quick actions">
            <div className={cell}>
                {phone ? <CallButton size="xs" customerId={customerId} number={phone} onCalled={onCalled} />
                       : <Phone className="h-4 w-4 opacity-40" />}
                Call
            </div>
            {onWhatsApp && <button type="button" onClick={onWhatsApp} className={cell}><MessageCircle className="h-4 w-4 text-green-600" />WhatsApp</button>}
            <button type="button" onClick={onNote} className={cell}><StickyNote className="h-4 w-4 text-amber-500" />Note</button>
            <button type="button" onClick={onStage} className={cn(cell, "text-primary")}><TrendingUp className="h-4 w-4" />Stage</button>
        </div>
    );
}

// ── next follow-up ───────────────────────────────────────────────────────

/** "Today", "Tomorrow", "in 3 days", "2 days overdue" — and whether it is late. */
export function followUpStatus(date, now = dayjs()) {
    if (!date) return null;
    const d = dayjs(date);
    if (!d.isValid()) return null;
    const days = d.startOf("day").diff(now.startOf("day"), "day");
    if (days < 0) return { text: `${-days} day${days === -1 ? "" : "s"} overdue`, overdue: true };
    if (days === 0) return { text: d.isBefore(now) ? "Overdue today" : "Today", overdue: d.isBefore(now) };
    if (days === 1) return { text: "Tomorrow", overdue: false };
    return { text: `in ${days} days`, overdue: false };
}

export function NextFollowUpCard({ date, needsOne, onUpdate }) {
    const st = followUpStatus(date);
    return (
        <section aria-label="Next follow-up"
            className={cn("rounded-xl border p-3",
                st?.overdue ? "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20"
                    : !date && needsOne ? "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20"
                    : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800")}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                    {st?.overdue || (!date && needsOne)
                        ? <AlertTriangle className={cn("mt-0.5 h-4 w-4", st?.overdue ? "text-red-600" : "text-amber-600")} />
                        : <CalendarClock className="mt-0.5 h-4 w-4 text-primary" />}
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Next follow-up</p>
                        {date ? (
                            <>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatDateTime(date)}</p>
                                <p className={cn("text-xs", st?.overdue ? "font-medium text-red-700 dark:text-red-300" : "text-gray-500")}>{st?.text}</p>
                            </>
                        ) : (
                            <p className="text-sm text-gray-600 dark:text-gray-300">{needsOne ? "None booked" : "Not needed at this stage"}</p>
                        )}
                    </div>
                </div>
                <button type="button" onClick={onUpdate}
                    className="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
                    {date ? "Update" : "Book"}
                </button>
            </div>
        </section>
    );
}

// ── collapsible section for the right-hand panel ────────────────────────

export function ContextSection({ title, defaultOpen = true, actions, children, id }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <section id={id} className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between gap-2 px-3 py-2">
                <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                    className="flex flex-1 items-center gap-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
                    {title}
                </button>
                {open && actions}
            </div>
            {open && <div className="px-3 pb-3">{children}</div>}
        </section>
    );
}

// ── tabs ─────────────────────────────────────────────────────────────────

/* Seventeen flat tabs grouped by what a rep is doing. The tab names are the
   page's own (activeTab), so every setActiveTab('Calls') elsewhere and every
   ?tab= link keeps working; only how they are presented changes. */
export const TAB_GROUPS = [
    { key: "activity", label: "Activity", tabs: ["All Activities", "CRM Events", "Notes", "Calls", "Tasks", "Emails", "WhatsApp", "Documents"] },
    { key: "enquiries", label: "Enquiries", tabs: ["Enquiries"] },
    { key: "sales", label: "Sales", tabs: ["Engagement", "Sourced Leads", "Referral Benefits"] },
    { key: "profile", label: "Profile", tabs: ["Hospital Profile", "Company Research", "Pain Points", "Custom Fields"] },
    { key: "handover", label: "CRE Handover", tabs: ["CRE Handover"] },
];
const SUB_LABEL = { "All Activities": "All", "CRM Events": "Stage changes" };

export function groupOf(tab) {
    return TAB_GROUPS.find((g) => g.tabs.includes(tab)) || TAB_GROUPS[0];
}

export function TabNav({ available, activeTab, onChange }) {
    const last = useRef({});
    const groups = TAB_GROUPS.map((g) => ({ ...g, tabs: g.tabs.filter((t) => available.includes(t)) }))
                             .filter((g) => g.tabs.length);
    const current = groups.find((g) => g.tabs.includes(activeTab)) || groups[0];
    last.current[current.key] = activeTab;
    return (
        <div className="border-b border-gray-200 dark:border-gray-700">
            <div role="tablist" aria-label="Customer sections" className="flex overflow-x-auto px-2">
                {groups.map((g) => (
                    <button key={g.key} type="button" role="tab" aria-selected={g.key === current.key}
                        onClick={() => onChange(last.current[g.key] && g.tabs.includes(last.current[g.key]) ? last.current[g.key] : g.tabs[0])}
                        className={cn("whitespace-nowrap border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors",
                            g.key === current.key ? "border-primary text-primary"
                                : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200")}>
                        {g.label}
                    </button>
                ))}
            </div>
            {current.tabs.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto px-3 pb-2 pt-1" role="tablist" aria-label={`${current.label} views`}>
                    {current.tabs.map((t) => (
                        <button key={t} type="button" role="tab" aria-selected={t === activeTab} onClick={() => onChange(t)}
                            className={cn("whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium",
                                t === activeTab ? "border-primary bg-brand-50 text-primary dark:bg-brand-900/20"
                                    : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-300")}>
                            {SUB_LABEL[t] || t}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
